/**
 * Dispatches the record header's title to the right renderer: the two-field
 * contact editor, the single-name editor, or a plain read-only title —
 * preserving the previous-name strikethrough a pending rename proposal shows
 * beneath it.
 *
 * The dispatcher owns the read-only gate so neither editor has to duplicate
 * it: a draft preview (unsaved record OR a proposal rename being previewed)
 * and a review surface (`hideActions`) both force read-only, because
 * `previousName` — the struck-through old name — only exists inside a draft,
 * and neither state should let the viewer edit a value being previewed or
 * reviewed rather than live.
 *
 * Routes on `entityType === CONTACT`, not `isPersonType`: `isPersonType` also
 * matches `ahoy_user`, a live, non-read-only, person-kinded type reachable
 * here but out of scope for the two-field editor. Every type other than
 * `ahoy_contact` — including `ahoy_user` — takes the single-name path, where
 * the renamed property is whichever one the display name resolves from, and a
 * name with no single writable source fails the gate closed to read-only.
 */

import { TruncatedText } from '@/lib/base/truncated-text';
import { CONTACT } from '@/lib/crm/core/entityTypes';
import type { EntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import type { CrmObject } from '@/types/crm';

import { useRecordDraft } from '../contexts/RecordDraftContext';
import { useRecordView } from '../contexts/RecordViewContext';
import { RecordTitleContactEditor } from './RecordTitleContactEditor';
import { RecordTitleSingleNameEditor } from './RecordTitleSingleNameEditor';

export interface EditableRecordTitleProps {
  object: CrmObject;
  /** Resolved entity config. Only `readOnly` is read, by the contact editor. */
  config: Pick<EntityTypeConfig, 'readOnly'>;
  entityType: string;
  /** Current display name rendered in the title. */
  displayName: string;
  /** The name a pending rename proposal is replacing, if any. */
  previousName: string | undefined;
}

export function EditableRecordTitle({
  object,
  config,
  entityType,
  displayName,
  previousName,
}: EditableRecordTitleProps) {
  const draft = useRecordDraft();
  const { hideActions } = useRecordView();

  // Covers unsaved drafts AND proposal-preview renames — `previousName` only
  // exists when `draft != null`, so the strikethrough branch is always
  // read-only.
  const titleReadOnly = hideActions || draft != null;

  let titleLine;
  if (titleReadOnly) {
    titleLine = (
      <TruncatedText
        className='text-lg md:text-xl font-semibold text-fg-primary'
        text={displayName}
      />
    );
  } else if (entityType === CONTACT) {
    titleLine = (
      <RecordTitleContactEditor
        object={object}
        config={config}
        displayName={displayName}
      />
    );
  } else {
    titleLine = (
      <RecordTitleSingleNameEditor object={object} displayName={displayName} />
    );
  }

  return (
    <>
      {titleLine}
      {previousName !== undefined && (
        <TruncatedText
          className='text-xs text-fg-tertiary line-through'
          text={previousName}
          aria-label={`Previously named ${previousName}`}
        />
      )}
    </>
  );
}
